import { useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { AppContext } from "../AppContextProvider";
import {
  getSocketBaseUrl,
  refreshSessionRequestForQuotes,
} from "../api/ProcurementClient";
import { useWorkQueuePurchaseOrders } from "./useWorkQueuePurchaseOrders";
import Rfq from "../models/Rfq";
import {
  mergeFetchedRfqIntoList,
  mergeRemoteRfqIntoList,
  snapshotRfqClientEpochs,
} from "../utils/mergeRfqIntoList";
import { refreshRfqInList } from "../utils/refreshLinkedRfqs";

/** Backup poll on list pages — sockets + visibility cover most updates. */
const WORK_QUEUE_POLL_MS = 60_000;

type WorkQueueChangedPayload = {
  rfq?: Rfq;
  rfqId?: number;
};

/** Window event re-emitted for every `work-queue-changed` socket message. */
const RFQ_CHANGED_EVENT = "procurement:rfq-changed";

/**
 * Subscribe to server-side RFQ changes (any tab / user). `rfqId` is null when
 * the server did not say which RFQ changed. Returns unsubscribe.
 */
export function subscribeRfqChanged(
  onChange: (rfqId: number | null) => void
): () => void {
  const listener = (event: Event) => {
    const rfqId = (event as CustomEvent<{ rfqId?: number }>).detail?.rfqId;
    onChange(rfqId ?? null);
  };
  window.addEventListener(RFQ_CHANGED_EVENT, listener);
  return () => window.removeEventListener(RFQ_CHANGED_EVENT, listener);
}

function rfqListFingerprint(rfqs: Rfq[]): string {
  return rfqs
    .map(
      (r) =>
        `${r.id}:${r.status}:${r.priority ?? ""}:${r.assignedToId ?? ""}:${r.note ?? ""}:${r.dueDate ?? ""}:${r.referenceNum ?? ""}:${(r.quotes ?? []).length}`
    )
    .join("|");
}

function rfqIdFromPath(pathname: string): number | null {
  const match = pathname.match(/^\/rfq\/(\d+)/);
  if (!match) {
    return null;
  }
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Keeps RFQ state fresh across clients via Socket.IO.
 * List pages also poll periodically; detail pages refetch the full RFQ graph.
 */
export function useWorkQueueAutoRefresh(pollList = true): void {
  const { setRfqs } = useContext(AppContext);
  const { refreshPurchaseOrders } = useWorkQueuePurchaseOrders();
  const location = useLocation();

  const detailRfqId = useMemo(
    () => rfqIdFromPath(location.pathname),
    [location.pathname]
  );

  const pollListRef = useRef(pollList);
  const detailRfqIdRef = useRef(detailRfqId);
  const setRfqsRef = useRef(setRfqs);
  const refreshPurchaseOrdersRef = useRef(refreshPurchaseOrders);
  pollListRef.current = pollList;
  detailRfqIdRef.current = detailRfqId;
  setRfqsRef.current = setRfqs;
  refreshPurchaseOrdersRef.current = refreshPurchaseOrders;

  const refreshListRef = useRef(async () => {
    if (!pollListRef.current) {
      return;
    }
    try {
      const epochsAtStart = snapshotRfqClientEpochs();
      const requestForQuotes = await refreshSessionRequestForQuotes();
      setRfqsRef.current((prev) => {
        if (rfqListFingerprint(prev) === rfqListFingerprint(requestForQuotes)) {
          return prev;
        }
        // Merge per-row so a poll started before a status change cannot wipe it.
        const prevById = new Map(prev.map((r) => [r.id, r]));
        return requestForQuotes.map((rfq) => {
          const local = prevById.get(rfq.id);
          if (!local) return rfq;
          return mergeFetchedRfqIntoList(
            [local],
            rfq,
            epochsAtStart.get(rfq.id) ?? 0
          )[0];
        });
      });
    } catch {
      /* ignore background refresh errors */
    }
  });

  useEffect(() => {
    const refreshList = () => {
      void refreshListRef.current();
    };

    const intervalId = pollList
      ? window.setInterval(() => {
          if (document.visibilityState === "hidden") return;
          refreshList();
        }, WORK_QUEUE_POLL_MS)
      : undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      refreshList();
      if (detailRfqIdRef.current != null) {
        void refreshRfqInList(detailRfqIdRef.current, setRfqsRef.current);
      }
      void refreshPurchaseOrdersRef.current();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pollList]);

  // Keep a single long-lived socket so navigating between pages does not flap
  // connect/disconnect (which drops inbox / work-queue live events).
  useEffect(() => {
    const socket: Socket = io(getSocketBaseUrl(), {
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    const onWorkQueueChanged = (payload: WorkQueueChangedPayload) => {
      const rfqId = payload?.rfq?.id ?? payload?.rfqId;
      window.dispatchEvent(
        new CustomEvent(RFQ_CHANGED_EVENT, { detail: { rfqId } })
      );
      if (!rfqId) {
        void refreshListRef.current();
        return;
      }

      const viewingDetailRfq = detailRfqIdRef.current === rfqId;

      // Prefer the broadcast payload, but respect local mutation grace so a
      // stale session load cannot snap status back to OPEN on the work queue.
      if (payload.rfq) {
        setRfqsRef.current((prev) => mergeRemoteRfqIntoList(prev, payload.rfq!));
      } else if (!viewingDetailRfq) {
        void refreshRfqInList(rfqId, setRfqsRef.current);
      }

      void refreshPurchaseOrdersRef.current();
    };
    socket.on("work-queue-changed", onWorkQueueChanged);

    return () => {
      socket.off("work-queue-changed", onWorkQueueChanged);
      socket.disconnect();
    };
  }, []);
}
